import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../contexts/UserContext";
import { SyllabusContext } from "../contexts/SyllabusContext";
import Sidebar from "../common/Sidebar";
import MobileTopbar from "../common/MobileTopbar";
import MobileSidebarDrawer from "../common/MobileSidebarDrawer";
import { FiSearch, FiX } from "react-icons/fi";
import { FaChevronLeft } from "react-icons/fa";
import Header from "./common/Header";
import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

const DEFAULT_SUBJECTS = [];
const DEFAULT_CHAPTERS = {
  default: [],
};

const sanitizeTextValue = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  const normalized = trimmed.toLowerCase();
  if (normalized === "undefined" || normalized === "null") {
    return "";
  }
  return trimmed;
};

const getGradeKeyForLearning = (grade) => {
  if (grade === null || grade === undefined) {
    return "10th Grade";
  }

  const gradeStr = String(grade).trim();
  if (!gradeStr.length) {
    return "10th Grade";
  }

  if (/grade/i.test(gradeStr)) {
    return gradeStr;
  }

  if (!/^\d+$/.test(gradeStr)) {
    return gradeStr;
  }

  const suffix = (() => {
    const number = Number(gradeStr);
    const remainder100 = number % 100;
    if (remainder100 >= 11 && remainder100 <= 13) {
      return "th";
    }
    switch (number % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  })();

  return `${gradeStr}${suffix} Grade`;
};

const getFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeKey = (value) => sanitizeTextValue(value).toLowerCase();

const normalizeSelfAssessmentLevels = (rawLevels = {}) => {
  if (!rawLevels || typeof rawLevels !== "object") {
    return {};
  }
  const normalized = {};
  Object.entries(rawLevels).forEach(([rawSubjectName, subjectValue]) => {
    const normalizedSubjectKey = normalizeKey(rawSubjectName);
    if (!normalizedSubjectKey) {
      return;
    }
    if (
      !subjectValue ||
      typeof subjectValue !== "object" ||
      Array.isArray(subjectValue)
    ) {
      normalized[normalizedSubjectKey] = {};
      return;
    }
    const normalizedChapters = {};
    Object.entries(subjectValue).forEach(([rawChapterName, chapterValue]) => {
      const normalizedChapterKey = normalizeKey(rawChapterName);
      if (!normalizedChapterKey) {
        return;
      }
      normalizedChapters[normalizedChapterKey] =
        chapterValue && typeof chapterValue === "object"
          ? { ...chapterValue }
          : {};
    });
    normalized[normalizedSubjectKey] = normalizedChapters;
  });
  return normalized;
};

const LEVEL_ID_TO_VALUE = {
  beginner: 1,
  intermediate: 2,
  proficient: 3,
  advanced: 4,
};

const NewLearnComponent2 = () => {
  const { userData, updateProfileDetails, fetchUserProfile } =
    useContext(UserContext);
  const syllabusContext = useContext(SyllabusContext);
  const {
    subjects: syllabusSubjects,
    subjectChapters,
    fetchSyllabus,
  } = syllabusContext || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const subjects =
    Array.isArray(syllabusSubjects) && syllabusSubjects.length > 0
      ? syllabusSubjects
      : DEFAULT_SUBJECTS;
  const [selectedSubject, setSelectedSubject] = useState(
    subjects[0] || DEFAULT_SUBJECTS[0]
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettingLevel, setIsSettingLevel] = useState(false);
  const queryParams = new URLSearchParams(location.search || "");
  const querySubjectParam = sanitizeTextValue(queryParams.get("subject"));
  const queryChapterParam = sanitizeTextValue(queryParams.get("chapter"));
  const queryFlowParam = queryParams.get("flow");
  const stateSubjectParam = sanitizeTextValue(location.state?.subject);
  const stateChapterParam = sanitizeTextValue(location.state?.chapter);
  const isChangeTopicFlow =
    location.state?.from === "change-topic" ||
    queryFlowParam === "change-topic";
  const subjectLabelText =
    stateSubjectParam ||
    querySubjectParam ||
    sanitizeTextValue(selectedSubject) ||
    "this subject";
  const hasTriggeredSyllabusFetchRef = useRef(false);
  const hasRequestedProfileRef = useRef(false);
  const [selfAssessmentLookup, setSelfAssessmentLookup] = useState({});
  const [selfAssessmentGrade, setSelfAssessmentGrade] = useState(null);
  const [isSelfAssessmentLoading, setIsSelfAssessmentLoading] = useState(true);

  useEffect(() => {
    if (hasRequestedProfileRef.current) return;
    if (typeof fetchUserProfile !== "function") return;
    hasRequestedProfileRef.current = true;
    Promise.resolve(fetchUserProfile()).catch((error) =>
      console.error("Failed to fetch user details", error)
    );
  }, [fetchUserProfile]);

  useEffect(() => {
    if (hasTriggeredSyllabusFetchRef.current) return;
    if (typeof fetchSyllabus === "function") {
      hasTriggeredSyllabusFetchRef.current = true;
      Promise.resolve(fetchSyllabus()).catch((error) =>
        console.error("Failed to refresh syllabus data", error)
      );
    }
  }, [fetchSyllabus]);

  useEffect(() => {
    const userId = userData?.userId;
    if (!userId) {
      setSelfAssessmentLookup({});
      setIsSelfAssessmentLoading(false);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      setSelfAssessmentLookup({});
      setIsSelfAssessmentLoading(false);
      return;
    }
    let isCancelled = false;
    const fetchSelfAssessment = async () => {
      setIsSelfAssessmentLoading(true);
      try {
        const response = await axios.get(
          `${baseUrl}/v2/user-self-assessment-new/${userId}`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (isCancelled) return;
        const payload =
          response.data?.result && typeof response.data.result === "object"
            ? response.data.result
            : response.data || {};
        const normalizedLevels = normalizeSelfAssessmentLevels(payload?.levels);
        setSelfAssessmentLookup(normalizedLevels);
        setSelfAssessmentGrade(
          payload?.grade && typeof payload.grade === "string"
            ? payload.grade
            : null
        );
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to fetch self assessment data:", error);
          setSelfAssessmentLookup({});
        }
      } finally {
        if (!isCancelled) {
          setIsSelfAssessmentLoading(false);
        }
      }
    };
    fetchSelfAssessment();
    return () => {
      isCancelled = true;
    };
  }, [userData?.userId]);

  const proficiencyLevels = [
    {
      id: "beginner",
      title: "BEGINNER",
      subtitle: "",
      description: "I need to learn this from the start.",
      height: "h-32", // Shortest
      value: 0,
    },
    {
      id: "intermediate",
      title: "INTERMEDIATE",
      subtitle: "",
      description: "I know the theory, but I struggle with problems.",
      height: "h-40", // Taller
      value: 20,
    },
    {
      id: "proficient",
      title: "PROFICIENT",
      subtitle: "",
      description: "I usually get it right, but I need to ace my tests.",
      height: "h-44", // Taller
      value: 40,
    },
    {
      id: "advanced",
      title: "ADVANCED",
      subtitle: "",
      description: "I am ready for hard problems.",
      height: "h-48", // Tallest
      value: 60,
    },
  ];

  const getLevelIdFromScore = (score) => {
    const numericScore = getFiniteNumber(score);
    if (numericScore === null || !proficiencyLevels.length) {
      return null;
    }
    let closestLevelId = proficiencyLevels[0].id;
    let smallestDiff = Math.abs(numericScore - proficiencyLevels[0].value);
    for (let index = 1; index < proficiencyLevels.length; index += 1) {
      const level = proficiencyLevels[index];
      const diff = Math.abs(numericScore - level.value);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestLevelId = level.id;
      }
    }
    return closestLevelId;
  };

  const getLevelIdFromValue = (levelValue) => {
    const numericLevel = getFiniteNumber(levelValue);
    if (numericLevel === null) {
      return null;
    }
    const matchedEntry = Object.entries(LEVEL_ID_TO_VALUE).find(
      ([, levelNumeric]) => levelNumeric === numericLevel
    );
    return matchedEntry ? matchedEntry[0] : null;
  };

  const getSelfAssessmentEntry = (subjectName, chapterName) => {
    const subjectKey = normalizeKey(subjectName);
    const chapterKey = normalizeKey(chapterName);
    if (!subjectKey || !chapterKey) {
      return null;
    }
    return selfAssessmentLookup?.[subjectKey]?.[chapterKey] || null;
  };

  const composeLearningLevelUpdate = ({
    subjectName,
    chapterName,
    levelId,
    loValue,
    shieldPercentage,
  }) => {
    const sanitizedSubjectName = sanitizeTextValue(subjectName);
    if (!sanitizedSubjectName || !chapterName) {
      return null;
    }
    const gradeKey = getGradeKeyForLearning(
      userData?.grade || selfAssessmentGrade || "10th Grade"
    );
    const currentLearningLevel = userData?.learning_current_level || {};
    const existingGradeData =
      (gradeKey && currentLearningLevel[gradeKey]) || {};
    const normalizedSubjectKey = sanitizedSubjectName.toLowerCase();
    let subjectStorageKey = sanitizedSubjectName;
    let existingSubjectEntry =
      existingGradeData[sanitizedSubjectName] ||
      existingGradeData[sanitizedSubjectName?.trim()];
    if (!existingSubjectEntry && existingGradeData) {
      const matchedKey = Object.keys(existingGradeData).find((key) => {
        if (typeof key !== "string") {
          return false;
        }
        return key.trim().toLowerCase() === normalizedSubjectKey;
      });
      if (matchedKey) {
        subjectStorageKey = matchedKey;
        existingSubjectEntry = existingGradeData[matchedKey];
      }
    }
    const nextSubjectEntry = {
      chapter: chapterName,
      lo:
        typeof loValue !== "undefined" && loValue !== null
          ? loValue
          : existingSubjectEntry?.lo ??
            existingSubjectEntry?.current_lo ??
            null,
      level: LEVEL_ID_TO_VALUE[levelId] || LEVEL_ID_TO_VALUE.beginner || 1,
      shield_percentage:
        typeof shieldPercentage === "number"
          ? shieldPercentage
          : getFiniteNumber(
              existingSubjectEntry?.shield_percentage ??
                existingSubjectEntry?.shieldPercentage
            ) ?? 0,
    };
    if (!gradeKey) {
      return null;
    }
    return {
      gradeKey,
      subjectKey: subjectStorageKey,
      updatedLearningLevel: {
        ...currentLearningLevel,
        [gradeKey]: {
          ...existingGradeData,
          [subjectStorageKey]: nextSubjectEntry,
        },
      },
    };
  };

  const persistLearningLevelUpdate = async ({
    subjectName,
    chapterName,
    levelId,
    loValue,
    shieldPercentage,
  }) => {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication token is missing");
    }
    const payload = composeLearningLevelUpdate({
      subjectName,
      chapterName,
      levelId,
      loValue,
      shieldPercentage,
    });
    if (!payload) {
      throw new Error("Unable to compose learning level update");
    }
    await axios.put(
      `${baseUrl}/v2/profile/update-profile`,
      {
        learning_current_level: payload.updatedLearningLevel,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    updateProfileDetails({
      learning_current_level: payload.updatedLearningLevel,
    });
    return payload.updatedLearningLevel;
  };

  const applySelfAssessmentSelection = async ({
    subjectName,
    chapterName,
    levelId,
    assessmentEntry,
    selfScoreValue,
  }) => {
    if (!subjectName || !chapterName || !levelId) {
      return;
    }
    const loValue =
      assessmentEntry?.currentLO ?? assessmentEntry?.currentLo ?? null;
    const latestScoreValue = getFiniteNumber(
      assessmentEntry?.latestScore ?? assessmentEntry?.latest_score
    );
    const normalizedSelfScore = getFiniteNumber(selfScoreValue);
    const resolvedShield =
      latestScoreValue !== null
        ? latestScoreValue
        : normalizedSelfScore !== null
        ? normalizedSelfScore
        : 0;
    try {
      await persistLearningLevelUpdate({
        subjectName,
        chapterName,
        levelId,
        loValue,
        shieldPercentage: resolvedShield,
      });
    } catch (error) {
      console.error("Failed to persist learning level update:", error);
      const fallback = composeLearningLevelUpdate({
        subjectName,
        chapterName,
        levelId,
        loValue,
        shieldPercentage: resolvedShield,
      });
      if (fallback) {
        updateProfileDetails({
          learning_current_level: fallback.updatedLearningLevel,
        });
      }
    }
  };

  const handleChapterClick = async (chapter) => {
    const subjectDisplayName =
      typeof selectedSubject === "string"
        ? selectedSubject
        : selectedSubject?.title || selectedSubject?.name || "";
    const chapterTitle =
      typeof chapter === "string"
        ? chapter
        : chapter?.title || chapter?.name || chapter?.chapter || "";
    const sanitizedSubjectName = sanitizeTextValue(subjectDisplayName);
    const sanitizedChapterTitle = sanitizeTextValue(chapterTitle);
    const subjectKeyForStorage =
      (subjectDisplayName && subjectDisplayName.trim()) || sanitizedSubjectName;
    if (sanitizedSubjectName && sanitizedChapterTitle && subjectKeyForStorage) {
      const assessmentEntry = getSelfAssessmentEntry(
        sanitizedSubjectName,
        sanitizedChapterTitle
      );
      const existingSelfScore = getFiniteNumber(
        assessmentEntry?.selfScore ?? assessmentEntry?.self_score
      );
      if (existingSelfScore !== null) {
        const derivedLevelId =
          getLevelIdFromScore(existingSelfScore) ||
          proficiencyLevels[0]?.id ||
          null;
        if (derivedLevelId) {
          try {
            await applySelfAssessmentSelection({
              subjectName: subjectKeyForStorage,
              chapterName: sanitizedChapterTitle,
              levelId: derivedLevelId,
              assessmentEntry,
              selfScoreValue: existingSelfScore,
            });
          } catch (error) {
            console.error("Failed to auto-set learning level:", error);
          }
        }
        navigateToNewLearn(
          subjectKeyForStorage,
          sanitizedChapterTitle,
          derivedLevelId
        );
        return;
      }
    }
    setSelectedLevel(null);
    setSelectedChapter(chapter);
    setIsModalOpen(true);
  };

  const handleLevelSelect = (levelId) => {
    setSelectedLevel(levelId);
  };

  const handleSetLevelAndStart = async () => {
    if (!selectedLevel || !selectedChapter) {
      return;
    }

    const levelDetails = proficiencyLevels.find(
      (level) => level.id === selectedLevel
    );
    const selfAssessmentScore = levelDetails?.value;

    if (selfAssessmentScore === undefined) {
      console.error("Invalid proficiency level selected");
      return;
    }

    const chapterTitle =
      typeof selectedChapter === "string"
        ? selectedChapter
        : selectedChapter?.title || selectedChapter?.name || "";

    if (!chapterTitle) {
      return;
    }

    const normalizedSubject = sanitizeTextValue(
      typeof selectedSubject === "string"
        ? selectedSubject
        : selectedSubject?.title || selectedSubject?.name || ""
    );
    const fallbackSubject = (
      subjects.find(
        (subject) => typeof subject === "string" && subject.trim().length > 0
      ) || ""
    ).trim();
    const subjectToSend = normalizedSubject || fallbackSubject;

    if (!subjectToSend) {
      console.error("No subject available for self assessment");
      return;
    }

    const subjectKey = subjectToSend;

    setIsSettingLevel(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        // Still navigate even if API call fails
        setIsSettingLevel(false);
        setIsModalOpen(false);
        navigateToNewLearn(subjectToSend, chapterTitle);
        return;
      }

      const userId = userData?.userId;
      if (!userId) {
        console.error("No user ID available for self assessment update");
        setIsSettingLevel(false);
        setIsModalOpen(false);
        navigateToNewLearn(subjectToSend, chapterTitle);
        return;
      }

      const selfAssessmentResponse = await axios.post(
        `${baseUrl}/v2/quiz/update-self-assessment-lo`,
        {
          user_id: userId,
          subject: subjectToSend,
          chapter: chapterTitle,
          self_assessment_score: selfAssessmentScore,
        },
        {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responsePayload =
        (selfAssessmentResponse?.data?.result &&
        typeof selfAssessmentResponse.data.result === "object"
          ? selfAssessmentResponse.data.result
          : null) ??
        (selfAssessmentResponse?.data &&
        typeof selfAssessmentResponse.data === "object"
          ? selfAssessmentResponse.data
          : {});
      const resolvedShieldPercentage = getFiniteNumber(
        responsePayload?.shield_percentage ?? responsePayload?.shieldPercentage
      );
      const resolvedLoValue = getFiniteNumber(
        responsePayload?.lo ??
          responsePayload?.current_lo ??
          responsePayload?.currentLo
      );
      const resolvedLevelId = getLevelIdFromValue(
        responsePayload?.level ??
          responsePayload?.level_id ??
          responsePayload?.levelId
      );

      await persistLearningLevelUpdate({
        subjectName: subjectKey,
        chapterName: chapterTitle,
        levelId: resolvedLevelId || selectedLevel,
        loValue: resolvedLoValue ?? 2,
        shieldPercentage: resolvedShieldPercentage ?? selfAssessmentScore,
      });

      await fetchUserProfile();
      setIsSettingLevel(false);
      setIsModalOpen(false);
      navigateToNewLearn(subjectToSend, chapterTitle);
    } catch (error) {
      console.error("Error updating learning level:", error);
      setIsSettingLevel(false);
      // Still navigate even if API call fails
      setIsModalOpen(false);
      navigateToNewLearn(subjectToSend, chapterTitle);
    }
  };

  const navigateToNewLearn = (subject, chapter, levelOverride = null) => {
    const params = new URLSearchParams({
      subject: subject,
      chapter: chapter,
      from: "new-learn-2",
    });

    navigate(`/new-learn?${params.toString()}`, {
      state: {
        from: "new-learn-2",
        subject: subject,
        chapter: chapter,
        level: levelOverride ?? selectedLevel,
      },
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLevel(null);
    setSelectedChapter(null);
  };

  const handleBackToNewLearn = () => {
    const subjectForBackNav =
      stateSubjectParam ||
      querySubjectParam ||
      sanitizeTextValue(selectedSubject);

    const selectedChapterTitle =
      selectedChapter &&
      (typeof selectedChapter === "string"
        ? sanitizeTextValue(selectedChapter)
        : sanitizeTextValue(
            selectedChapter?.title ||
              selectedChapter?.name ||
              selectedChapter?.chapter
          ));

    const chapterForBackNav =
      selectedChapterTitle || stateChapterParam || queryChapterParam || "";

    const params = new URLSearchParams();
    if (subjectForBackNav) {
      params.set("subject", subjectForBackNav);
    }
    if (chapterForBackNav) {
      params.set("chapter", chapterForBackNav);
    }

    const statePayload = {
      from: "new-learn-2",
    };

    if (subjectForBackNav) {
      statePayload.subject = subjectForBackNav;
    }
    if (chapterForBackNav) {
      statePayload.chapter = chapterForBackNav;
    }

    const queryString = params.toString();
    navigate(`/new-learn${queryString ? `?${queryString}` : ""}`, {
      state: statePayload,
    });
  };

  useEffect(() => {
    const availableSubjects =
      Array.isArray(syllabusSubjects) && syllabusSubjects.length > 0
        ? syllabusSubjects
        : DEFAULT_SUBJECTS;

    if (!availableSubjects.length) return;

    setSelectedSubject((current) =>
      current && availableSubjects.includes(current)
        ? current
        : availableSubjects[0]
    );
  }, [syllabusSubjects]);

  useEffect(() => {
    if (!selectedChapter) return;

    const chaptersArray = Array.isArray(subjectChapters?.[selectedSubject])
      ? subjectChapters[selectedSubject]
      : [];

    if (!chaptersArray.length) {
      setSelectedChapter(null);
      return;
    }

    const resolvedSelectedTitle = sanitizeTextValue(
      typeof selectedChapter === "string"
        ? selectedChapter
        : selectedChapter?.title ||
            selectedChapter?.name ||
            selectedChapter?.chapter ||
            ""
    );

    if (!resolvedSelectedTitle) {
      setSelectedChapter(null);
      return;
    }

    const stillExists = chaptersArray.some((entry) => {
      const entryTitle =
        typeof entry === "string"
          ? entry
          : entry?.title || entry?.name || entry?.chapter || "";
      return (
        sanitizeTextValue(entryTitle).toLowerCase() ===
        resolvedSelectedTitle.toLowerCase()
      );
    });

    if (!stillExists) {
      setSelectedChapter(null);
    }
  }, [selectedSubject, selectedChapter, subjectChapters]);

  // Initialize subject & chapter from query params, e.g. ?subject=Science&chapter=Chemical+Reactions+and+Equations
  useEffect(() => {
    const availableSubjects =
      Array.isArray(subjects) && subjects.length > 0
        ? subjects
        : DEFAULT_SUBJECTS;

    if (!availableSubjects.length) return;

    const searchParams = new URLSearchParams(location.search || "");
    const rawSubject = searchParams.get("subject");
    const rawChapter = searchParams.get("chapter");
    const isSubjectInvalid =
      !rawSubject ||
      rawSubject.toLowerCase() === "undefined" ||
      rawSubject.toLowerCase() === "null";

    if (isSubjectInvalid) {
      return;
    }

    const normalizedSubject = rawSubject.replace(/\+/g, " ").trim();
    const normalizedChapter =
      rawChapter &&
      rawChapter.toLowerCase() !== "undefined" &&
      rawChapter.toLowerCase() !== "null"
        ? rawChapter.replace(/\+/g, " ").trim()
        : null;

    if (!normalizedSubject) {
      return;
    }

    // Only apply if the subject exists in our tabs
    if (!availableSubjects.includes(normalizedSubject)) {
      return;
    }

    setSelectedSubject(normalizedSubject);

    // Resolve chapters for this subject from subjectChapters
    if (
      normalizedChapter &&
      subjectChapters &&
      Array.isArray(subjectChapters[normalizedSubject]) &&
      subjectChapters[normalizedSubject].length > 0
    ) {
      const chaptersArray = subjectChapters[normalizedSubject];

      const matchIndex = chaptersArray.findIndex((chapterEntry) => {
        const title =
          (typeof chapterEntry === "string"
            ? chapterEntry
            : chapterEntry?.title || chapterEntry?.name || "") || "";
        return title.trim().toLowerCase() === normalizedChapter.toLowerCase();
      });

      if (matchIndex !== -1) {
        const chapterEntry = chaptersArray[matchIndex];
        const title =
          (typeof chapterEntry === "string"
            ? chapterEntry
            : chapterEntry?.title || chapterEntry?.name || "") || "";
        if (title) {
          setSelectedChapter({
            id: matchIndex + 1,
            title,
          });
        }
      }
    }
  }, [location.search, subjects, subjectChapters]);

  const chaptersForSelectedSubject = (() => {
    if (
      selectedSubject &&
      subjectChapters &&
      Array.isArray(subjectChapters[selectedSubject]) &&
      subjectChapters[selectedSubject].length > 0
    ) {
      return subjectChapters[selectedSubject];
    }

    return (
      DEFAULT_CHAPTERS[selectedSubject] ||
      DEFAULT_CHAPTERS.default ||
      DEFAULT_CHAPTERS[subjects[0]] ||
      []
    );
  })();

  const chapters = chaptersForSelectedSubject.map((chapterName, index) => {
    if (typeof chapterName === "string") {
      return { id: index + 1, title: chapterName };
    }
    if (chapterName && typeof chapterName === "object") {
      return {
        id: chapterName.id || index + 1,
        title: chapterName.title || chapterName.name || `Chapter ${index + 1}`,
      };
    }
    return { id: index + 1, title: `Chapter ${index + 1}` };
  });

  // Filter chapters based on search query
  const filteredChapters = chapters.filter((chapter) =>
    chapter.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isSelfAssessmentLoading) {
    return (
      <div className="h-[100dvh] flex overflow-hidden bg-white">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex-1 flex items-center justify-center">
            <SimpleThreeDotsLoader message="Loading your learning data..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex overflow-hidden bg-white">
      {/* Sidebar - shown only on md+ screens */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile Drawer */}
      <MobileSidebarDrawer
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Topbar */}
        <MobileTopbar
          onMenuClick={() => setIsMobileMenuOpen(true)}
          showMenuButton={true}
          showProfileButton={false}
          showEditButton={false}
        />
        <div className="fixed top-0 left-0 right-0 z-20 h-1/10 pointer-events-none">
          <Header />
        </div>
        <div className="flex flex-col h-full w-full">
          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto px-4 md:px-8 pb-10 pt-[2%]">
            <div className="max-w-6xl mx-auto">
              <div className="mb-4 mt-2">
                <button
                  type="button"
                  onClick={handleBackToNewLearn}
                  className="inline-flex items-center gap-2 text-sm md:text-base z-99 text-[#0D99FF] hover:text-[#6351E0] font-medium transition-colors"
                >
                  <FaChevronLeft className="w-4 h-4" />
                  <span>Back to Topics</span>
                </button>
              </div>
              {/* Subject Navigation Tabs */}
              <div className="mb-6 md:mb-8 w-full flex justify-center">
                {isChangeTopicFlow ? (
                  <div className="w-full md:w-auto bg-gray-50 border border-gray-200 rounded-2xl px-4 md:px-6 py-3 text-center">
                    <p className="text-sm md:text-base text-gray-600">
                      Changing topic for{" "}
                      <span className="font-semibold text-gray-900">
                        {subjectLabelText}
                      </span>
                      . Pick a chapter below.
                    </p>
                  </div>
                ) : (
                  <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 -mx-2 px-2 md:mx-0 md:px-0 justify-center items-center">
                    {subjects.map((subject) => (
                      <button
                        key={subject}
                        onClick={() => setSelectedSubject(subject)}
                        className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                          selectedSubject === subject
                            ? "bg-[#0D99FF] text-white shadow-md"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {subject}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search Bar */}
              <div className="mb-6 md:mb-8">
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="What chapter are you learning this week?"
                    className="w-full pl-12 pr-4 py-3 md:py-4 rounded-2xl border-2 border-gray-200 focus:border-[#0D99FF] focus:outline-none text-sm md:text-base"
                  />
                </div>
              </div>

              {/* Chapters Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredChapters.map((chapter) => (
                  <button
                    key={chapter.id}
                    onClick={() => handleChapterClick(chapter)}
                    className="bg-white border-2 border-gray-200 rounded-xl p-4 md:p-6 hover:border-[#0D99FF] hover:shadow-lg transition-all duration-200 hover:-translate-y-1 text-left"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-lg md:text-xl font-bold text-gray-400">
                        {chapter.id}.
                      </span>
                      <span className="text-base md:text-lg font-semibold text-gray-900 flex-1">
                        {chapter.title}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* No results message */}
              {filteredChapters.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    No chapters found matching "{searchQuery}"
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Proficiency Level Selection Modal */}
      {isModalOpen && selectedChapter && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl p-6 md:p-8 relative">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition p-2"
              aria-label="Close modal"
            >
              <FiX className="w-6 h-6" />
            </button>

            {/* Modal Content */}
            <div className="mt-4">
              {/* Question */}
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">
                How do you feel about {selectedChapter.title}?
              </h2>

              {/* Proficiency Level Cards */}
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6 mb-8 md:mb-12 items-end">
                {proficiencyLevels.map((level) => {
                  const isSelected = selectedLevel === level.id;
                  return (
                    <button
                      key={level.id}
                      onClick={() => handleLevelSelect(level.id)}
                      className={`flex-1 ${
                        level.height
                      } rounded-2xl p-4 md:p-6 transition-all duration-200 ${
                        isSelected
                          ? "bg-[#0D99FF] text-white shadow-lg scale-105"
                          : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                      }`}
                    >
                      <div className="flex flex-col h-full justify-between">
                        <div>
                          <h3
                            className={`text-sm md:text-base font-bold uppercase mb-2 ${
                              isSelected ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {level.title}
                          </h3>
                          <p
                            className={`text-sm md:text-base font-semibold mb-2 ${
                              isSelected ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {level.subtitle}
                          </p>
                          <p
                            className={`text-xs md:text-sm ${
                              isSelected ? "text-white/90" : "text-gray-600"
                            }`}
                          >
                            {level.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Set Level & Start Learning Button */}
              <div className="flex justify-center mt-6 md:mt-8 pt-6 md:pt-8">
                {isSettingLevel ? (
                  <div className="flex flex-col items-center gap-3">
                    <SimpleThreeDotsLoader message="Updating your learning plan..." />
                  </div>
                ) : (
                  <button
                    onClick={handleSetLevelAndStart}
                    disabled={!selectedLevel}
                    className={`px-8 md:px-12 py-3 md:py-4 rounded-xl md:rounded-2xl text-base md:text-lg font-semibold transition-all duration-200 ${
                      selectedLevel
                        ? "bg-[#0D99FF] text-white hover:bg-[#6351E0] hover:shadow-lg hover:-translate-y-0.5"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Set Level & Start Learning
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewLearnComponent2;
